/**
 * Draft ⇄ wire enum translation — the ONE place that knows the backend's PascalCase strings for
 * the plan's phase type and its execution mode.
 *
 * ── The scheduling mode is a translation again, and it is an ALLOW-LIST in both directions ────
 * `SchedulingMode` holds two members the backend will accept: `DateBased` and `SelfPaced`. The
 * sequence engine that used to be the second one is deleted
 * (`docs/adr/2026-08-16-self-paced-plans-replace-the-sequence-engine.md`), and its name parses
 * nowhere. Both live members now have a canvas here — the weekday grid and the tape — so the mode
 * is mapped rather than gated to one value.
 *
 * `schedulingModeFromApi` still answers `null` for a mode this build cannot name, and
 * `readPlanDocument` refuses on that answer. That is the half that must not become tolerant: a
 * third wire mode defaulted to `'date'` would be opened on the weekday grid and CONVERTED by the
 * first whole-tree save, because the save re-emits whatever `PlanDraft.mode` says.
 *
 * Ported from `move-with-me-frontend/.../plans/builder/model/draftEnums.ts`, renamed for what it
 * holds. Screens, projections and the store stay in this app's own lower-case vocabulary; only the
 * DTO mappers call through here.
 *
 * `…FromApi` are tolerant — case-insensitive, with a stated default — because the full-template
 * response types every one of these as a nullable string. `…ToApi` are exhaustive over the union,
 * so widening the union without teaching the mapper is a compile error rather than a `undefined`
 * on the wire.
 *
 * The two `…ToApi` functions have no caller in PR-A1: nothing here saves. They ship with their
 * inverses anyway, because the pair is what makes the round trip reviewable in one file — a
 * mapping whose two halves land in different pull requests is a mapping nobody checks for
 * symmetry.
 */

import type { Translate } from '~/shared/lib/localeTranslate';

import type { PhaseType, PlanExecutionMode, PlanSchedulingMode } from './planDraft.types';

/**
 * Draft mode → the wire's `SchedulingMode` name. Exhaustive over the union, so adding a canvas
 * without teaching this mapper is a compile error rather than an `undefined` on the wire.
 */
const SCHEDULING_MODE_TO_API: Record<PlanSchedulingMode, string> = {
  date: 'DateBased',
  selfpaced: 'SelfPaced',
};

/**
 * Written on every save: the field is required on POST, and echoing it on PATCH is not a change
 * (see `draftToRequest`). A mode-CHANGING PATCH on a live fork is refused with `plan_mode_locked`,
 * which is the backend's own R1 — the mode is fixed at creation.
 */
export function schedulingModeToApi(mode: PlanSchedulingMode): string {
  return SCHEDULING_MODE_TO_API[mode];
}

/** The inverse, keyed on the PascalCase name the backend serialises — matched EXACTLY. */
const SCHEDULING_MODE_FROM_API: Record<string, PlanSchedulingMode> = {
  DateBased: 'date',
  SelfPaced: 'selfpaced',
};

/**
 * Is this wire value exactly this mode?
 *
 * The one place any surface in this app asks that question. Five of them asked it in four different
 * spellings — two matched `DateBased` case-insensitively after a `toLowerCase()`, three matched the
 * PascalCase name exactly — and the two answers disagree about the same string, so a mis-cased mode
 * opened the date grid on one screen and fell through as unknown on the next.
 *
 * **Exact, matching `schedulingModeFromApi` above.** The backend serialises the enum by NAME, so a
 * mis-cased value is not a mode the contract produces: it is a shape this build cannot account for,
 * and the conservative reading of one is "neither mode" rather than "probably the common one". Three
 * tests already pinned that reading (`assignTarget.test.ts`, `planLibraryRow.test.ts`); the two
 * lenient readers were the outliers and are gone.
 *
 * Whitespace is trimmed and nothing else is, exactly as `schedulingModeFromApi` does it.
 *
 * ── Ask it POSITIVELY, always ────────────────────────────────────────────────────────────────
 * Every caller matches one name and none says "not the other one". The enum has two members today
 * and a third would otherwise inherit whichever branch happened to be the `else` — silently, on the
 * screen that was written first. A mode this build cannot name should get neither mode's treatment,
 * which only two positive tests can express.
 *
 * Note the ASYMMETRY with `schedulingModeFromApi`, and that it is deliberate: a BLANK reads as
 * `'date'` there, because a legacy plan with no stored mode is weekly in every other respect and
 * refusing it would shut the builder on plans it has always opened. A blank is not `DateBased`
 * HERE, because this answers "what did the wire say" rather than "which canvas do we open".
 */
export function schedulingModeIs(
  wireValue: string | null | undefined,
  mode: PlanSchedulingMode,
): boolean {
  return (wireValue?.trim() ?? '') === SCHEDULING_MODE_TO_API[mode];
}

/**
 * Which canvas the wire's `SchedulingMode` selects, or `null` when this build has no canvas for it.
 *
 * **An ABSENT mode reads as weekly, and that is the one tolerated value.** A persisted plan cannot
 * arrive without one — the backend serialises the enum by name, so every stored plan carries
 * `"DateBased"` or `"SelfPaced"` verbatim — so a blank comes from a legacy or hand-assembled shape
 * that is weekly in every other respect, and refusing it would shut the builder on plans it has
 * always opened.
 *
 * Everything else is an allow-list, matched EXACTLY — unlike the tolerant, case-insensitive mappers
 * below it. A mode this build cannot name must NOT fall back to `'date'`: the save is whole-tree and
 * re-emits `PlanDraft.mode`, so opening an unknown mode on the weekday grid would convert the plan
 * on the trainer's first save. A mis-cased name is a shape the backend does not serialise, so
 * accepting it would be guessing which canvas a caller meant.
 */
export function schedulingModeFromApi(
  value: string | null | undefined,
): PlanSchedulingMode | null {
  const named = value?.trim() ?? '';
  if (named === '') return 'date';
  // `Object.hasOwn` before indexing: a bare index walks the prototype chain, so `"constructor"` off
  // the wire yields a function that is neither null nor falsy and would be returned as a mode.
  return Object.hasOwn(SCHEDULING_MODE_FROM_API, named)
    ? (SCHEDULING_MODE_FROM_API[named] ?? null)
    : null;
}

/**
 * A plan's length in its own unit — „3 tygodnie" on the calendar, „5 cykli" at its own pace.
 *
 * The one wording of it: the create dialog's template rows, the assign dialog's head and its
 * self-paced review all say a plan's length, and three copies of the key choice had grown. The noun
 * follows the mode rather than being baked into a key (execution ADR §7). `t` arrives as a parameter.
 */
export function planLengthLabel(mode: PlanSchedulingMode, count: number, t: Translate): string {
  return t(mode === 'selfpaced' ? 'web:planBuilder.cycles' : 'web:planBuilder.weeks', { count });
}

const PHASE_TYPE_TO_API: Record<PhaseType, string> = {
  build: 'Build',
  deload: 'Deload',
};

export function phaseTypeToApi(type: PhaseType): string {
  return PHASE_TYPE_TO_API[type];
}

/** Normalise a wire `PhaseType` to the draft kind. Unknown or null → `'build'`, the default. */
export function phaseTypeFromApi(value: string | null | undefined): PhaseType {
  return value?.toLowerCase() === 'deload' ? 'deload' : 'build';
}

/**
 * The backend's `ExerciseType`, lower-cased, as a CLOSED set.
 *
 * **Deliberately duplicated from `entities/exercise-library/model/exerciseType.ts`.** Entities of
 * the same layer may not import each other (execution ADR §2), and the two are genuinely separate
 * concerns that happen to share a vocabulary: the library owns the type as something a trainer
 * FILTERS by, with a label and a display order; the draft carries it only so a card can draw a
 * glyph. Neither needs the other's half. The duplication is six words, and the alternative is a
 * shared entity existing solely to hold them.
 *
 * Keep the two in step; both are exhaustive over the same six-member C# enum.
 */
const EXERCISE_TYPE_KEYS: readonly string[] = [
  'strength',
  'bodyweight',
  'cardio',
  'mobility',
  'conditioning',
  'custom',
];

/**
 * Normalise a wire `ExerciseType` to the draft's lower-case key, or `null` when this build has
 * never heard of it.
 *
 * A CLOSED parse rather than a bare `?.toLowerCase()`. Lower-casing whatever arrived meant an
 * unrecognised value became a key nothing could render — a glyph lookup missing silently, and a
 * value the type says is one of six holding something else entirely. `null` is the honest answer
 * for "this build does not know", and it is already the value for an exercise carrying no type.
 */
export function exerciseTypeKeyFromApi(value: string | null | undefined): string | null {
  if (value == null) return null;
  const key = value.trim().toLowerCase();
  return EXERCISE_TYPE_KEYS.includes(key) ? key : null;
}

const EXECUTION_MODE_FROM_API: Record<string, PlanExecutionMode> = {
  remote: 'remote',
  elastic: 'elastic',
  stationary: 'stationary',
};

/**
 * Normalise a wire `ExecutionMode` to the draft's delivery kind.
 *
 * Unlike the two above there is NO default: unknown or null → `null`, meaning "this build does not
 * know how the client trains". The only consumer warns the trainer about their own bookings, and
 * guessing either way is worse than staying silent — a wrong `'stationary'` cries wolf on every
 * remote plan, a wrong `'remote'` hides a real calendar change.
 */
export function executionModeFromApi(value: string | null | undefined): PlanExecutionMode | null {
  if (value == null) return null;
  const key = value.trim().toLowerCase();
  // `Object.hasOwn` before indexing: a bare index walks the prototype chain, so `"constructor"` off
  // the wire yields a function that is neither null nor falsy and would be returned as a mode.
  return Object.hasOwn(EXECUTION_MODE_FROM_API, key) ? (EXECUTION_MODE_FROM_API[key] ?? null) : null;
}
